import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { ResponseProjetAiMessageScenario } from '../../models/response-project';

const PREFIX = 'tenders.responses.wizard.';

const LOADING_SCENARIOS = new Set([
  ResponseProjetAiMessageScenario.UPLOADING,
  ResponseProjetAiMessageScenario.CATEGORIZING,
  ResponseProjetAiMessageScenario.FINALIZING,
]);

const COUNT_SCENARIOS = new Set([
  ResponseProjetAiMessageScenario.RECEIVED,
  ResponseProjetAiMessageScenario.ADDED,
  ResponseProjetAiMessageScenario.REMOVED,
]);

@Component({
  selector: 'app-response-project-wizard-ai-typewriter',
  templateUrl: './response-project-wizard-ai-typewriter.component.html',
  styleUrls: ['./response-project-wizard-ai-typewriter.component.scss'],
})
export class ResponseProjectWizardAiTypewriterComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('source', { read: ElementRef }) private sourceRef?: ElementRef<HTMLElement>;

  @Input() scenario: ResponseProjetAiMessageScenario = ResponseProjetAiMessageScenario.WELCOME;
  @Input() fileCount = 0;
  @Input() addedPhraseIndex = 0;
  @Input() removedPhraseIndex = 0;

  get translationKey(): string {
    const s = ResponseProjetAiMessageScenario;
    const oneMany = this.fileCount <= 1 ? 'one' : 'many';
    const slot = (i: number) => Math.min(i, 2);

    switch (this.scenario) {
      case s.WELCOME:
        return `${PREFIX}ai-message`;
      case s.RECEIVED:
        return `${PREFIX}ai-received-${oneMany}`;
      case s.ADDED:
        return `${PREFIX}ai-added-${slot(this.addedPhraseIndex)}-${oneMany}`;
      case s.REMOVED:
        return `${PREFIX}ai-removed-${slot(this.removedPhraseIndex)}-${oneMany}`;
      case s.UPLOADING:
        return `${PREFIX}ai-uploading`;
      case s.CATEGORIZING:
        return `${PREFIX}ai-categorizing`;
      case s.FINALIZING:
        return `${PREFIX}ai-finalizing`;
      case s.WORKING:
        return `${PREFIX}ai-working`;
      case s.READY:
        return `${PREFIX}ai-ready`;
      case s.ASK_INTERNAL:
        return `${PREFIX}ai-ask-internal`;
      default:
        return '';
    }
  }

  get translationParams(): Record<string, number> {
    return COUNT_SCENARIOS.has(this.scenario) && this.fileCount > 1 ? { count: this.fileCount } : {};
  }

  typedText = '';
  showCursor = false;
  showDots = false;
  dotLabel = '';

  private readonly timers: ReturnType<typeof setTimeout>[] = [];
  private domPollAttempts = 0;
  private viewReady = false;
  private firstRender = true;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.restart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (changes['scenario'] || changes['fileCount'] || changes['addedPhraseIndex'] || changes['removedPhraseIndex']) {
      this.restart();
    }
  }

  ngOnDestroy(): void {
    this.timers.forEach(clearTimeout);
  }

  private restart(): void {
    this.timers.forEach(clearTimeout);
    this.timers.length = 0;
    this.domPollAttempts = 0;
    const t = setTimeout(() => this.readDomText(), 0);
    this.timers.push(t);
  }

  private readDomText(): void {
    const text = this.sourceRef?.nativeElement?.textContent?.trim() ?? '';
    if (text) {
      this.startWithDots(text);
      return;
    }
    if (this.domPollAttempts++ > 50) return;
    const t = setTimeout(() => this.readDomText(), 50);
    this.timers.push(t);
  }

  private startWithDots(text: string): void {
    if (this.firstRender) {
      this.firstRender = false;
      this.showDots = false;
      this.dotLabel = '';
      this.display(text);
      return;
    }

    this.typedText = '';
    this.showCursor = false;
    this.showDots = true;
    this.dotLabel = '';

    const t = setTimeout(() => {
      this.display(text);
    }, 700);
    this.timers.push(t);
  }

  private display(text: string): void {
    if (LOADING_SCENARIOS.has(this.scenario)) {
      this.showDots = true;
      this.dotLabel = text;
      return;
    }
    this.showDots = false;
    this.dotLabel = '';
    this.runTypewriter(text);
  }

  private runTypewriter(text: string): void {
    this.typedText = '';
    this.showCursor = true;
    let i = 0;
    const step = () => {
      if (i > text.length) return;
      this.typedText = text.slice(0, i);
      if (i === text.length) {
        const t = setTimeout(() => (this.showCursor = false), 1200);
        this.timers.push(t);
      } else {
        const t = setTimeout(step, 18);
        this.timers.push(t);
      }
      i++;
    };
    step();
  }
}
